import { isOne, isZero } from '@utils/validations'

import { NameDto } from './NameDto'
import {
  PlatformTypeEnum,
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from './PlatformTypeEnum'
import { SwapFreeLevelType } from './SwapFreeLevelEnum'
import { TradingAccountPlatformOverview } from './TradingAccountDetailDto'
import { CurrencyType } from './WalletDto'

const liveAccountGroupType = 'Live'

export interface CurrencyBalance {
  currency: CurrencyType
  balance: number
  isTransferDisabled: boolean
}

export interface BaseTradingAccountServer {
  id: number
  name: string
  publicName: string
}

export interface BaseTradingAccount {
  id: string
  name: string
  nickname?: string | null
  currency: NameDto<CurrencyType>
  server: BaseTradingAccountServer
  tickmillCompany: NameDto
  status: NameDto
  country: NameDto<string>
  accountType: NameDto
  platformType: NameDto<PlatformTypeEnum> & { dynamicLeverage: boolean }
  leverageType: NameDto
  parentPlatformType?: BaseTradingAccount['platformType']
  accountGroupType: NameDto | null
  externalId?: string
}

export const isPlatformTypeMT4 = (data?: BaseTradingAccount): boolean => {
  return isMT4PlatformType(getPlatformTypeId(data))
}

export const isPlatformTypeMT5 = (data?: BaseTradingAccount): boolean => {
  return isMT5PlatformType(getPlatformTypeId(data))
}

export const isPlatformTypeTickmillTrader = (data: BaseTradingAccount): boolean => {
  return isTMTPlatformType(getPlatformTypeId(data))
}

export const isPlatformTypeTradingView = (data?: BaseTradingAccount): boolean => {
  return isTradingViewPlatformType(getPlatformTypeId(data))
}

export const getPlatformTypeId = (data?: BaseTradingAccount): PlatformTypeEnum | undefined => {
  return (data?.platformType ?? data?.parentPlatformType)?.id
}

export interface TradingAccount extends BaseTradingAccount {
  platformOverview: {
    login: string
    group: string
    equity: number
    balance: number
    lastEndOfDayBalance: number
    usedMargin: number
    freeMargin: number
    marginLevel: number
    balances: CurrencyBalance[]
    availableToWithdraw: number
    availableToWithdrawUSD: number
    netLiquidationValueUSD: number
    reportingCurrency: CurrencyType
    balanceInDisplayCurrency?: number
  }
  client: NameDto<string>
  introducingBroker: NameDto<string> | null
  accountGroup: {
    id: string
    name: string
    currency: NameDto<CurrencyType>
    restrictions: []
    classificationType: NameDto
    tickmillCompany: NameDto
  }
  wallet: NameDto<string>
  accountGroupType: NameDto
  leverageType: NameDto
  restrictions: NameDto[]
  businessRules: CampaignBusinessRuleDto[]
  isSwapFree: boolean
  balance: number
  equity: number
  createdDate: string
  updatedDate: string
  campaigns: TradingAccountCampaign[]
  swapsSaved: number
  swapFreeLevel: SwapFreeLevelType
  ratingValue: number
  swapFreeOption: {
    id: string
    countryId: string
    green: number
    red: number
    daysTillSuspended: number
    enabled: boolean
  } | null
  isSuspendedFromSwapFree: boolean
  manualOverride: boolean
  lotsToTrade: number | null
  startRedZone: string | null
}

export interface TradingAccountTotals {
  totalBalanceInDisplayCurrency: number
}

export interface CampaignBusinessRuleDto {
  businessRule: BusinessRuleDto
  businessRuleValue: number | null
}

interface BusinessRuleDto {
  id: number
  name: string | null
  businessRuleType: NameDto
  hasValue: boolean
}

export interface TradingAccountCampaign {
  defaultCurrencyId: string
  id: string
  minDepositAmount: number
  name: string
  key: string
  status: NameDto<number>
  internalOnly: boolean
}

export interface UpdateTradingAccountLeverage {
  leverageTypeId: number
}

export interface UpdateTradingAccountNickname {
  tradingAccountId: string
  nickname: string | null
}

export interface UpdateDemoTradingAccount {
  leverageTypeId: number
  accountGroupId: string
}

export interface DemoTradingAccount extends BaseTradingAccount {
  form: TradingAccountForm
  accountGroupServerId: string
  accountGroup: NameDto<string>
  leverage: NameDto
  lead: NameDto<string>
  created: string
  updated: string
  platformOverview: TradingAccountPlatformOverview
  introducingBroker: NameDto<string> | null
}

export interface AddDemoTradingAccount {
  id: string
  publicName: string
  dns: string
  platformType: NameDto
}

export interface TradingAccountForm {
  firstName: string
  lastName: string
  email: any
  phoneNumber: any
  depositAmount: number
}
export enum TradingAccountStatusType {
  Approved = 1,
  ReadOnly = 4,
  CloseOnly = 8,
}
export interface ResetTradingAccountPassword {
  tradingAccountId: string
  changeInvestorPassword: boolean
}

export enum BusinessRulesEnum {
  TransferFromPercentageProfit = 102,
}

export function isTradingAccountStatusApproved(status: NameDto): boolean {
  return status.id === TradingAccountStatusType.Approved
}

export function isTradingAccountStatusReadOnly(status: NameDto): boolean {
  return status.id === TradingAccountStatusType.ReadOnly
}

export function isTradingAccountStatusCloseOnly(status: NameDto): boolean {
  return status.id === TradingAccountStatusType.CloseOnly
}

export const isLiveAccount = (accountGroupType: NameDto | null): boolean =>
  accountGroupType?.name === liveAccountGroupType

export const selectDefaultTradingAccount = (
  tradingAccounts: TradingAccount[]
): TradingAccount | undefined => {
  const newTradingAccounts = tradingAccounts.filter((x) => isTMTPlatformType(x.platformType.id))

  if (isZero(newTradingAccounts.length)) {
    return undefined
  }

  if (isOne(newTradingAccounts.length)) {
    const [tradingAccount] = newTradingAccounts
    return tradingAccount
  }

  return newTradingAccounts.reduce((prev: TradingAccount, curr: TradingAccount) =>
    (prev.platformOverview.balance || 0) > (curr.platformOverview.balance || 0) ? prev : curr
  )
}

export const hasRestrictions = (
  account?: TradingAccount | DemoTradingAccount
): account is TradingAccount => {
  if (!account) {
    return false
  }
  return 'restrictions' in account
}
